import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../components/primitives/SMLButton';
import { sendAllMessages as s } from './sendAllMessages';
import type { SendAllState } from './useSendAll';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Send to all sheet; each part uses its own slice of it */
import styles from './SendAll.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The foot of the sheet (design c `.sh-f`): one line, then "Send to N services", "Retry N items"
 * or Close while a send works. The button is off without the rights line.
 */
const SendAllFooterComponent: React.FC<{ state: SendAllState; onClose: () => void }> = ({ state, onClose }) => {
  const intl = useIntl();
  const { footer, rightsOk, isSending, send } = state;

  if (footer.kind === 'working') {
    return (
      <div className={styles.foot}>
        <span className={styles.footMeta}>{intl.formatMessage(s.metaWorking)}</span>
        <SMLButton onClick={onClose}>{intl.formatMessage(s.close)}</SMLButton>
      </div>
    );
  }

  let meta = intl.formatMessage(s.metaReplaces);
  if (footer.kind === 'nothing') {
    meta = intl.formatMessage(footer.reason === 'noService' ? s.metaNoService : s.metaAllLive);
  } else if (!rightsOk) {
    meta = intl.formatMessage(s.metaRights);
  } else if (footer.kind === 'retry') {
    meta = intl.formatMessage(s.metaRetry);
  }
  const canSend = footer.kind !== 'nothing' && rightsOk && !isSending;
  let label = intl.formatMessage(s.sendTo, { count: footer.kind === 'retry' ? 0 : footer.services });
  if (footer.kind === 'retry') {
    label = intl.formatMessage(s.retryItems, { count: footer.items });
  }
  if (isSending) {
    label = intl.formatMessage(s.sendBusy);
  }
  const allLive = footer.kind === 'nothing' && footer.reason === 'allLive';

  return (
    <div className={styles.foot}>
      <span className={styles.footMeta} data-testid="send-all-meta">
        {meta}
      </span>
      <SMLButton
        variant="primary"
        disabled={!canSend}
        onClick={() => (footer.kind === 'send' || footer.kind === 'retry' ? send(footer.targets) : undefined)}
      >
        <span data-testid="send-all-send">{label}</span>
      </SMLButton>
      <SMLButton className={styles.footSecondary} onClick={onClose}>
        {intl.formatMessage(allLive ? s.done : s.cancel)}
      </SMLButton>
    </div>
  );
};

export const SendAllFooter = TypedMemo(SendAllFooterComponent);
